import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeftIcon, ArrowRightIcon } from '@heroicons/react/20/solid'
import { useLazyQuery } from '@apollo/client'
import { GET_TRIP_INFO } from 'graphql/route/queries'
import loadingImage from 'assets/images/boat-loading.gif'
import CaptainSchedule from '../captainScheduleList'
import { RoutesIcon } from '../../../components/icons'

export default function CaptainAssignmentDetails() {
    const navigate = useNavigate()
    const { t } = useTranslation()
    const { route_id, trip_id } = useParams()
    const [routeTrip, setRouteTrip] = useState(null)
    const handleBack = () => {
        navigate('/metro-routes/metro-route-details/' + route_id)
    }

    const [getRouteTripList, { loading: routeTripLoading, error: routeTripError }] = useLazyQuery(
        GET_TRIP_INFO,
        {
            fetchPolicy: 'network-only',
            notifyOnNetworkStatusChange: true,
            onCompleted: data => {
                if (data.getBoatRouteTrip.success && data.getBoatRouteTrip.code === '200') {
                    setRouteTrip(data.getBoatRouteTrip.trip)
                }
            },
        }
    )

    useEffect(() => {
        searchBoatsRoutes()
    }, [route_id])

    const searchBoatsRoutes = () => {
        getRouteTripList({
            variables: {
                tripId: trip_id
            },
        })
    }
    return (
        <>
            <div className="mb-6 flex justify-between items-center">
                <button
                    onClick={handleBack}
                    type="button"
                    className="inline-flex items-center gap-x-0.5 rounded-md text-sm"
                >
                    <ArrowLeftIcon aria-hidden="true" className="-ml-0.5 h-5 w-5 rtl:rotate-180" />
                    {t('Back')}
                </button>
            </div>
            {routeTripLoading && (
                <div className="flex flex-col justify-center items-center bg-white h-96 mt-16">
                    <img alt="logo" src={loadingImage} className="w-[250px]" />
                    <p className="pt-3 text-primary font-semibold">Loading, please wait...</p>
                </div>
            )}
            {routeTripError && (
                <div className="flex justify-center items-center bg-white py-5">
                    <div className="text-lg text-center">{t('Failed to fetch routes!')}</div>
                </div>
            )}
            {!routeTripLoading && !routeTripError && routeTrip == null && (
                <div className="flex justify-center items-center bg-white py-5">
                    <div className="text-lg text-center">{t('No route found!')}</div>
                </div>
            )}
            {!routeTripLoading && !routeTripError && routeTrip != null && (
                <div className="mt-8">
                    <div className="md:flex md:items-center md:space-x-5">
                        <div className="flex items-start space-x-5">
                            <div className="flex-shrink-0 mb-4">
                                <div className="relative">
                                    <div className="w-12 h-12 bg-gray-50 p-4 flex justify-center items-center">
                                    <RoutesIcon className="w-6 h-6 fill-primary group-hover:fill-white" />
                                    </div>
                                    <span aria-hidden="true" className="absolute inset-0 rounded-lg shadow-inner" />
                                </div>
                            </div>
                            <div className="pt-1">
                                <p className="text-sm font-medium text-gray-500">{t('Route Name')}</p>
                                <h1 className="text-md font-bold text-gray-900">
                                    {
                                        routeTrip.route?.route_name?.en
                                    }
                                </h1>
                            </div>
                        </div>
                        <span className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-full bg-white text-primary">
                            <ArrowRightIcon aria-hidden="true" className="-ml-0.5 h-5 w-5" />
                        </span>
                        <div className="flex items-start space-x-5">
                            <div className="flex-shrink-0 mb-4">
                                <div className="relative">
                                    <div className="w-12 h-12 bg-gray-50 p-4 flex justify-center items-center">
                                        <svg
                                            height="100%"
                                            viewBox="0 0 512 512"
                                            width="100%"
                                            className="fill-primary"
                                            xmlns="http://www.w3.org/2000/svg"
                                            id="fi_3710370"
                                        >
                                            <g id="Navigator">
                                                <path d="m456.59 74.3682a86.939 86.939 0 0 0 -132.2211 112.0053l56.8331 79.5625a17.1 17.1 0 0 0 27.832 0l56.833-79.5625a86.6665 86.6665 0 0 0 -9.277-112.0053zm-61.472 89.9763a28.5 28.5 0 1 1 28.5-28.5 28.5014 28.5014 0 0 1 -28.5 28.5z"></path>
                                                <path d="m395.118 314.9019a17.0965 17.0965 0 0 0 -17.098 17.0981v36.46a60.4419 60.4419 0 1 1 -120.8838 0v-155.08a94.6382 94.6382 0 1 0 -189.2763 0v80.62a17.0982 17.0982 0 0 0 34.1963 0v-80.62a60.4419 60.4419 0 1 1 120.8837 0v155.08a94.6382 94.6382 0 0 0 189.2764 0v-36.46a17.0965 17.0965 0 0 0 -17.0983-17.0981z"></path>
                                                <path d="m84.9578 352.9019a55.0981 55.0981 0 1 0 55.0982 55.0981 55.1578 55.1578 0 0 0 -55.0982-55.0981z"></path>
                                            </g>
                                        </svg>
                                    </div>
                                    <span aria-hidden="true" className="absolute inset-0 rounded-lg shadow-inner" />
                                </div>
                            </div>
                            <div className="pt-1">
                                <p className="text-sm font-medium text-gray-500">{t('Trip Name')}</p>
                                <h1 className="text-md font-bold text-gray-900">
                                    {routeTrip.trip_name?.en}
                                </h1>
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-wrap items-end justify-between gap-4 mt-2">
                        <div className="max-sm:w-full sm:flex-1">
                            <div className="md:flex md:items-center sm:justify-between md:space-x-5">
                                <div className="flex items-start space-x-5">
                                    <div className="px-4 sm:px-0">
                                        <h3 className="text-lg font-semibold leading-7 text-primary">
                                            {t('Captains')}
                                        </h3>
                                        <p className="mt-1 max-w-2xl text-sm leading-6 text-gray-500">
                                            {t('View Details about the captain, and add a new captain.')}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex space-y-4 space-y-reverse sm:flex-row-reverse justify-end sm:space-x-3 sm:space-y-0 sm:space-x-reverse md:mt-0 md:flex-row md:space-x-3">
                                    <a
                                        href={`/metro-routes/captain-schedule-form/0/${routeTrip.route.route_id}/${trip_id}/${routeTrip.trip_name?.en}`}
                                        className="h-[36]  bg-primary focus-visible:outline-primary justify-center rounded-md px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                                    >
                                        {t('Add Captain')}
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="mt-3 border-t border-gray-100">
                        <div className="bg-white flex flex-wrap items-end justify-between gap-4 border border-gray-100 rounded-t-lg overflow-hidden py-6 px-3">
                            <div className="max-sm:w-full sm:flex-1">
                                <CaptainSchedule
                                    routeId={route_id}
                                    tripID={trip_id}
                                    routeName={routeTrip.route.route_name?.en}
                                    tripName={routeTrip.trip_name?.en}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    )
}
